import {
  DEFAULT_PRODUCT_TYPE,
  type FilterableProductType,
  getProductTypeBasePath,
  getProductTypeQueryValue,
  isProductTypeSlug,
  PRODUCT_TYPE_QUERY_KEY,
  resolveProductType,
} from '@/helpers/products-filter-type'
import { CATALOG_URL_PARAMS, type SearchParamsReader } from './search-params'

/**
 * Estado dos filtros de `/cursos`, derivado inteiramente da URL
 * (`/cursos/[area]/[categoria]?tipo=&c=`). A URL é a única fonte da verdade.
 */
export interface CatalogFilters {
  productType: FilterableProductType
  areaSlug: string
  categorySlug: string
  workload: string
}

export type CatalogFiltersChange = Partial<CatalogFilters>

const CATALOG_ROOT = '/cursos'

/** Query params preservados ao trocar filtros (busca, ordenação e videoaula). */
const PRESERVED_PARAMS = [
  CATALOG_URL_PARAMS.search,
  CATALOG_URL_PARAMS.sort,
  CATALOG_URL_PARAMS.videoClass,
] as const

export function parseCatalogFilters(
  pathname: string,
  searchParams: SearchParamsReader
): CatalogFilters {
  const segments = pathname.startsWith(CATALOG_ROOT)
    ? pathname.slice(CATALOG_ROOT.length).split('/').filter(Boolean)
    : []
  const firstSegment = decodeURIComponent(segments.at(0) ?? '')
  const isTypeSlug = isProductTypeSlug(firstSegment)

  return {
    productType: resolveProductType({
      slug: firstSegment,
      type: searchParams.get(PRODUCT_TYPE_QUERY_KEY),
    }),
    areaSlug: isTypeSlug ? '' : firstSegment,
    categorySlug: isTypeSlug ? '' : decodeURIComponent(segments.at(1) ?? ''),
    workload: searchParams.get(CATALOG_URL_PARAMS.workload)?.trim() ?? '',
  }
}

/**
 * Aplica uma mudança aos filtros atuais respeitando a hierarquia:
 * trocar o tipo limpa área, subárea e carga horária; trocar a área limpa a
 * subárea.
 */
export function applyCatalogFiltersChange(
  current: CatalogFilters,
  change: CatalogFiltersChange
): CatalogFilters {
  const typeChanged =
    change.productType !== undefined &&
    change.productType !== current.productType
  const base: CatalogFilters = typeChanged
    ? {
        productType: change.productType ?? DEFAULT_PRODUCT_TYPE,
        areaSlug: '',
        categorySlug: '',
        workload: '',
      }
    : current
  const areaChanged =
    change.areaSlug !== undefined && change.areaSlug !== base.areaSlug

  return {
    ...base,
    ...change,
    categorySlug: areaChanged
      ? (change.categorySlug ?? '')
      : (change.categorySlug ?? base.categorySlug),
  }
}

export function buildCatalogFiltersHref(
  filters: CatalogFilters,
  currentSearchParams: SearchParamsReader
): string {
  const path = filters.areaSlug
    ? [
        CATALOG_ROOT,
        ...[filters.areaSlug, filters.categorySlug]
          .filter(Boolean)
          .map(encodeURIComponent),
      ].join('/')
    : getProductTypeBasePath(filters.productType)

  const params = new URLSearchParams()
  const typeValue = filters.areaSlug
    ? getProductTypeQueryValue({
        slug: filters.areaSlug,
        type: filters.productType,
      })
    : ''
  if (typeValue) {
    params.set(PRODUCT_TYPE_QUERY_KEY, typeValue)
  }
  if (filters.workload) {
    params.set(CATALOG_URL_PARAMS.workload, filters.workload)
  }
  for (const key of PRESERVED_PARAMS) {
    const value = currentSearchParams.get(key)
    if (value) {
      params.set(key, value)
    }
  }

  // A paginação é descartada de propósito: novos filtros voltam à página 1.
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

export function countActiveCatalogFilters(filters: CatalogFilters): number {
  return [
    filters.productType !== DEFAULT_PRODUCT_TYPE,
    filters.areaSlug,
    filters.categorySlug,
    filters.workload,
  ].filter(Boolean).length
}
